// src/components/ProviderCard.jsx
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ImageSequenceScroller from '../components/ImageSequenceScroller';

const API_URL = process.env.REACT_APP_API_URL;

const fetchProviderThumbnails = async ({ queryKey }) => {
  const [, providerId] = queryKey;
  try {
    const response = await axios.get(`${API_URL}/api/provider/${providerId}/thumbnails/`, {
      withCredentials: true,
    });
    return response.data.map(thumb => ({
      src: thumb.thumbnail_url,
      alt: `Thumbnail for provider ${providerId}`,
    }));
  } catch (error) {
    console.error('Thumbnail fetch error:', error);
    return [];
  }
};

export default function ProviderCard({
  provider,
  handleOrderClick,
  categories = []
}) {
  const queryClient = useQueryClient();

  const { data: providerImages = [], isLoading: imagesLoading } = useQuery({
    queryKey: ['providerThumbnails', provider.id],
    queryFn: fetchProviderThumbnails,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    enabled: !!provider.id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Compute display category
  const uniqueCategorySlugs = [
    ...new Set(provider.services?.map(s => s.category).filter(Boolean) ?? [])
  ];

  const displayCategory =
    provider.services?.length > 0 && provider.services[0].category
      ? uniqueCategorySlugs.length > 1
        ? (categories.find(c => c.is_general)?.name ?? 'General Services')
        : provider.services[0].category_name ?? 'Not set'
      : 'Not set';

  // Compute min charge
  const minCharge =
    provider.services?.length > 0 && provider.services[0].category
      ? uniqueCategorySlugs.length > 1
        ? Math.min(
            ...provider.services.map(s =>
              s.custom_minimum_charge ??
              (categories.find(c => c.is_general)?.default_minimum_charge ?? 300)
            )
          )
        : provider.services[0].custom_minimum_charge ??
          (categories.find(c => c.slug === provider.services[0].category)
            ?.default_minimum_charge ?? 300)
      : 300;

    // Safe Realistic Rating with proper fallback
  let averageRating = 4.7;
  let ratingCount = Math.floor(Math.random() * 11) + 10; // 10–20

  const rawRating = provider.average_rating;

  if (rawRating != null && !isNaN(rawRating) && Number(rawRating) > 0) {
    averageRating = Number(rawRating);
    ratingCount = provider.rating_count != null && Number(provider.rating_count) > 0
      ? Number(provider.rating_count)
      : Math.floor(Math.random() * 11) + 10;
  } else {
    // Fallback when rating is 0, null, undefined or invalid
    averageRating = 4.5 + Math.random() * 0.5; // 4.5 to 5.0
  }

  const fallbackImage = [{
    src: provider.images || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg',
    alt: provider.business_name || provider.username,
  }];

  const imagesToDisplay = providerImages.length > 0 ? providerImages : fallbackImage;

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group border-border bg-card">
      {/* Image header */}
      <CardHeader className="p-0 relative h-48 bg-muted overflow-hidden">
        {imagesLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <ImageSequenceScroller
              images={imagesToDisplay}
              interval={3000}
            />
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />

            {/* Promoted Badge */}
            {provider.is_promoted && (
              <Badge className="absolute top-3 left-3 z-20 bg-yellow-500 hover:bg-yellow-600 text-black font-bold shadow-md">
                PROMOTED
              </Badge>
            )}

            {/* Rating - Top Right */}
            <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-black/80 text-white px-3 py-1 rounded-full text-xs font-medium shadow-md">
              ★ {averageRating.toFixed(1)}
              <span className="text-gray-300">({ratingCount})</span>
            </div>
          </>
        )}
      </CardHeader>

      {/* Content */}
      <CardContent className="p-5 space-y-3">
        <div>
          <h3 className="text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors line-clamp-1">
            {provider.business_name || provider.username}
          </h3>
          <div className="mt-1 space-y-0.5">
            {provider.service_provided && (
              <p className="text-sm text-primary font-medium">
                {provider.service_provided}
              </p>
            )}
            <p className="text-sm text-violet-600 dark:text-violet-400">
              {displayCategory}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div>
            <span className="font-medium text-foreground">Location:</span> {provider.location || 'Kenya'}
          </div>
          <div>
            <span className="font-medium text-foreground">Starts at:</span>{' '}
            <span className="text-green-600 dark:text-green-400 font-semibold">
              {minCharge} KES
            </span>
          </div>
        </div>

        {provider.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {provider.description}
          </p>
        )}
      </CardContent>

      {/* Actions */}
      <CardFooter className="p-5 pt-0 flex gap-3">
        <Button
          onClick={() => handleOrderClick(provider)}
          className="flex-1"
          size="sm"
        >
          Order
        </Button>
        <Button
          variant="outline"
          size="sm"
          asChild
        >
          <Link to={`/provider/${provider.slug}`}>
            View Profile
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
